import React, { useState, useEffect } from 'react';
import './OrderNow.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaComment, FaAngleDown, FaClock, FaBirthdayCake, FaPalette, FaRulerCombined } from 'react-icons/fa';
import { FaLocationDot, FaCookie } from 'react-icons/fa6';

import axios from "axios";

const API_PATH = 'https://sweetdelight-react.asdesignsgalaxy.com/api/phpmailer/order.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const OrderNow = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    deliveryDate: '',
    pickupDate: '',
    deliveryTime: '',
    productType: '',
    flavor: '',
    sizeQuantity: '',
    frosting: '',
    filling: '',
    customText: '',
    deliveryAddress: '',
    pickupLocation: '',
    specialInstructions: '',
  });

  const [isDelivery, setIsDelivery] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validation logic remains the same
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.phone.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.phone)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.productType) validationErrors.push('Product type is required');
      if (!formData.flavor) validationErrors.push('Flavor is required');
      if (!formData.sizeQuantity) validationErrors.push('Size/Quantity is required');
      if (!formData.frosting) validationErrors.push('Frosting type is required');
      if (isDelivery && !formData.deliveryDate) validationErrors.push('Delivery date is required');
      if (!isDelivery && !formData.pickupDate) validationErrors.push('Pickup date is required');
      if (!formData.deliveryTime) validationErrors.push('Time is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Create FormData object to send
      const dataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        dataToSend.append(key, value);
      });
      dataToSend.append('deliveryMethod', isDelivery ? 'delivery' : 'pickup');

      const response = await axios({
        method: 'post',
        url: API_PATH,
        data: dataToSend,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.data || !response.data.sent) {
        throw new Error(response.data?.message || 'Form submission failed');
      }

      setMailSent(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        deliveryDate: '',
        pickupDate: '',
        deliveryTime: '',
        productType: '',
        flavor: '',
        sizeQuantity: '',
        frosting: '',
        filling: '',
        customText: '',
        deliveryAddress: '',
        pickupLocation: '',
        specialInstructions: ''
      });
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.message || 
               err.response?.statusText || 
               err.message || 
               'Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rest of your component remains the same
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="order-now">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            <Heading title={'Custom Order Request'} subTitle={"Create Your Perfect Dessert"} linear sideHeading />

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="phone"><FaPhone /></label>
                <input
                  type="tel"
                  name="phone"
                  className="box"
                  id="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="deliveryMethod"><FaLocationDot /></label>
                  <select
                    className="select-box"
                    id="deliveryMethod"
                    name="deliveryMethod"
                    value={isDelivery ? 'delivery' : 'pickup'}
                    onChange={(e) => setIsDelivery(e.target.value === 'delivery')}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="delivery">Delivery</option>
                    <option value="pickup">Pickup</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {isDelivery ? (
                <div className="input-field">
                  <label htmlFor="deliveryDate"><FaCalendar /></label>
                  <input
                    type="text"
                    name="deliveryDate"
                    placeholder="Delivery Date"
                    id="deliveryDate"
                    className="box"
                    onFocus={(e) => e.target.type = 'date'}
                    onBlur={(e) => e.target.type = 'text'}
                    value={formData.deliveryDate}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              ) : (
                <div className="input-field">
                  <label htmlFor="pickupDate"><FaCalendar /></label>
                  <input
                    type="text"
                    name="pickupDate"
                    placeholder="Pickup Date"
                    id="pickupDate"
                    className="box"
                    onFocus={(e) => e.target.type = 'date'}
                    onBlur={(e) => e.target.type = 'text'}
                    value={formData.pickupDate}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <div className="input-field">
                <label htmlFor="deliveryTime"><FaClock /></label>
                <input
                  type="text"
                  name="deliveryTime"
                  placeholder="Preferred Time"
                  id="deliveryTime"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.deliveryTime}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="productType"><FaBirthdayCake /></label>
                  <select
                    className="select-box"
                    id="productType"
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Product Type</option>
                    <option value="Cake">Cake</option>
                    <option value="Cupcakes">Cupcakes</option>
                    <option value="Cookies">Cookies</option>
                    <option value="Pastries">Pastries</option>
                    <option value="Other">Other Desserts</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="flavor"><FaCookie /></label>
                  <select
                    className="select-box"
                    id="flavor"
                    name="flavor"
                    value={formData.flavor}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Flavor</option>
                    <option value="Chocolate">Chocolate</option>
                    <option value="Vanilla">Vanilla</option>
                    <option value="Red Velvet">Red Velvet</option>
                    <option value="Strawberry">Strawberry</option>
                    <option value="Lemon">Lemon</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="sizeQuantity"><FaRulerCombined /></label>
                <input
                  type="text"
                  name="sizeQuantity"
                  className="box"
                  id="sizeQuantity"
                  placeholder="Size/Quantity"
                  value={formData.sizeQuantity}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="frosting"><FaPalette /></label>
                  <select
                    className="select-box"
                    id="frosting"
                    name="frosting"
                    value={formData.frosting}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Frosting Type</option>
                    <option value="Buttercream">Buttercream</option>
                    <option value="Fondant">Fondant</option>
                    <option value="Cream Cheese">Cream Cheese</option>
                    <option value="Whipped Cream">Whipped Cream</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="filling"><FaPalette /></label>
                  <select
                    className="select-box"
                    id="filling"
                    name="filling"
                    value={formData.filling}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Filling (Optional)</option>
                    <option value="Fruit">Fruit</option>
                    <option value="Custard">Custard</option>
                    <option value="Ganache">Ganache</option>
                    <option value="Jam">Jam</option>
                    <option value="None">None</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="customText"><FaComment /></label>
                <input
                  type="text"
                  name="customText"
                  className="box"
                  id="customText"
                  placeholder="Custom Text"
                  value={formData.customText}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className='input-box'>
              {isDelivery ? (
                <div className="input-field">
                  <label htmlFor="deliveryAddress"><FaLocationDot /></label>
                  <input
                    type="text"
                    name="deliveryAddress"
                    className="box"
                    id="deliveryAddress"
                    placeholder="Delivery Address"
                    value={formData.deliveryAddress}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>
              ) : (
                <div className="select">
                  <div className="select-container">
                    <label htmlFor="pickupLocation"><FaLocationDot /></label>
                    <select
                      className="select-box"
                      id="pickupLocation"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Pickup Location</option>
                      <option value="Main Bakery">Main Bakery</option>
                      <option value="Downtown Branch">Downtown Branch</option>
                      <option value="Mall Kiosk">Mall Kiosk</option>
                    </select>
                    <div className="icon-container"><FaAngleDown className="icon" /></div>
                  </div>
                </div>
              )}
            </div>

            <div className="input-field text-area">
              <label htmlFor="specialInstructions"><FaComment /></label>
              <textarea
                cols="30"
                rows="2"
                name="specialInstructions"
                className="box"
                id="specialInstructions"
                placeholder="Special Instructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Custom Order'}
            </button>

            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && (
              <div className="alert success">
                <span>Your custom order request has been submitted!</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default OrderNow;